/**
 * WaterfallChart — single canonical waterfall component for the whole app.
 *
 * Used by:
 *   - PortfolioPerformance.LossWaterfallCard       (Potential → losses → Delivered)
 *   - FacilityCovenants.CFADS waterfall            (Revenue → opex → CFADS → debt → Net)
 *   - any future waterfall (variance, P&L attribution, etc.)
 *
 * Why centralised: the same handful of bugs kept surfacing on each waterfall
 * we built — empty-area tooltip dead-zone, transparent spacer getting
 * colour-mapped, value labels invisible on zero-height bars, palette drift
 * between Commercial and Technical. Fixing them in this component means
 * every waterfall behaves identically across the app.
 *
 * Step kinds:
 *   'start'    — pillar from zero; sets the running total
 *   'subtotal' — pillar showing a mid-waterfall checkpoint
 *   'end'      — pillar showing the final running total
 *   'loss'     — floating bar, subtracts |value| from running total
 *   'gain'     — floating bar, adds |value| to running total
 *
 * Each `steps[]` item: { label, value, kind, color? }
 *   `color` (optional) overrides the default per-kind palette below.
 *
 * Other props:
 *   formatValue : (n) => string — how to format both axis ticks and bar labels
 *   height      : px (default 300)
 *   colors      : theme palette object from useThemeColors()
 *   summary     : optional element rendered in the card header
 *   title       : section header (overline)
 *   caption     : caption under the header
 */

import React, { useMemo } from 'react';
import { Box, Card, Stack, Typography, Skeleton } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { useAxesTooltip, ChartsTooltipContainer } from '@mui/x-charts/ChartsTooltip';


// ── Canonical palette ──────────────────────────────────────────────────────
// Aligned with CommercialOverview.CAUSE_COLOR so the same concept reads in
// the same colour across the app. Import this map from elsewhere if you
// need to colour a non-waterfall element the same way.
export const WATERFALL_PALETTE = {
  // Cumulative pillars
  start:    '#94a3b8',   // slate (neutral upper bound — e.g. "Potential", "Revenue")
  subtotal: '#3b82f6',   // blue  (mid-waterfall checkpoint — e.g. "CFADS")
  end:      '#22c55e',   // green (final — e.g. "Delivered", "Net cash")
  // Floating change bars
  loss:     '#ef4444',   // red   (matches Commercial "Neg. price" / curtailment)
  gain:     '#22c55e',   // green (matches Commercial "Generation")
};

// Energy-flavour palette — when the waterfall is a curtailment story you
// often want Econ vs Grid curtailment with the same colours as the rest of
// the Technical / Commercial pages.
export const ENERGY_PALETTE = {
  potential: '#94a3b8',  // slate — same as WATERFALL_PALETTE.start
  delivered: '#22c55e',  // green — same as Commercial "Generation"
  econ_curt: '#ef4444',  // red   — same as Commercial "Neg. price"
  grid_curt: '#3b82f6',  // blue  — same as Commercial "Network"
  thermal:   '#f59e0b',  // amber — same as Commercial "Thermal limit"
  other:     '#6b7280',  // grey
};


// ── Custom barLabel slot ───────────────────────────────────────────────────
// Default MUI X BarLabel renders the value centred *inside* the bar, which
// makes labels unreadable on thin / zero-height bars. This slot renders the
// label just ABOVE the bar top so:
//   - 0-value bars still surface their number (clamped above the baseline)
//   - thin loss bars don't have their numbers crushed inside a 3 px strip
//   - tall pillars get a clean header label rather than something floating
//     in the middle of the bar
//
// SVG note: y is the bar's TOP edge (smaller y = higher in chart space), so
// (y − 6) puts the label 6 px above the bar.
function AboveBarLabel(props) {
  // `height` of the bar is intentionally unused — we only need (x, y, width)
  // to position the label above the top edge of the bar.
  const { x, y, width, value, seriesId, ownerState } = props;
  const sid = seriesId || ownerState?.seriesId;
  // Spacer is invisible bookkeeping — never label it.
  if (sid === 'spacer') return null;
  if (value == null || value === '') return null;
  // Two-line support: "value\nsubLabel" renders the sub-line (e.g. the
  // step's % of potential) in smaller, dimmer type under the value — keeps
  // percentages with the bar instead of cluttering the x-axis.
  const [main, sub] = String(value).split('\n');
  // Clamp so the label can't drift off the top of the chart area.
  const ty = Math.max(sub ? 22 : 11, (y ?? 0) - (sub ? 17 : 6));
  const cx = (x ?? 0) + (width ?? 0) / 2;
  return (
    <text
      x={cx}
      y={ty}
      textAnchor="middle"
      dominantBaseline="text-after-edge"
      style={{
        fontSize: 11,
        fontWeight: 600,
        fill: '#94a3b8',
        fontVariantNumeric: 'tabular-nums',
        pointerEvents: 'none',
      }}
    >
      {main}
      {sub && (
        <tspan x={cx} dy={11} style={{ fontSize: 9.5, fontWeight: 500, fillOpacity: 0.85 }}>
          {sub}
        </tspan>
      )}
    </text>
  );
}


// Default series labels — override per-usage via the `seriesLabels` prop.
// Generic defaults work fine for CFADS (Revenue/Subtract/Add/CFADS/Net), but
// other waterfalls (e.g. constraint Pareto) want context-specific words
// like "Constraint" instead of "Gain" and "Total" instead of "End".
const DEFAULT_SERIES_LABELS = {
  start: 'Start',
  subtotal: 'Subtotal',
  end: 'End',
  loss: 'Loss',
  gain: 'Gain',
};


// ── Main component ─────────────────────────────────────────────────────────
export default function WaterfallChart({
  steps,
  height = 300,
  formatValue = (v) => String(Math.round(v)),
  colors = {},
  title,
  caption,
  summary,
  loading = false,
  emptyMessage = 'No data for this period.',
  // Per-usage series label overrides — e.g. { gain: 'Constraint', end: 'Total' }.
  seriesLabels: seriesLabelsProp,
  // External highlight: pass a step `label` value to fade everything except
  // the bar with that label. Used by ConstraintParetoCard for row-hover →
  // chart-highlight cross-component sync.
  highlightLabel,
}) {
  // Memoize so its identity is stable across renders — otherwise the
  // `series` useMemo below would see a new object every render and rebuild.
  const seriesLabels = useMemo(
    () => ({ ...DEFAULT_SERIES_LABELS, ...(seriesLabelsProp || {}) }),
    [seriesLabelsProp],
  );

  // Compute the controlled `highlightedItem` for MUI X — translates the
  // caller's `highlightLabel` (a step label string) into a {seriesId,
  // dataIndex} pair the BarChart understands. When null, the chart falls
  // back to native hover behaviour.
  const highlightedItem = useMemo(() => {
    if (!highlightLabel || !steps?.length) return null;
    const idx = steps.findIndex((s) => s.label === highlightLabel);
    if (idx < 0) return null;
    // Series ids are per-step (`s<idx>`) — see the series useMemo.
    return { seriesId: `s${idx}`, dataIndex: idx };
  }, [steps, highlightLabel]);
  // Build the dataset with per-row running totals and dataKey-specific cells.
  const rows = useMemo(() => {
    if (!steps?.length) return [];
    let running = 0;
    return steps.map((s) => {
      const raw = Number(s.value) || 0;
      let bar = 0, spacer = 0;
      if (s.kind === 'start') {
        bar = Math.abs(raw);
        running = bar;
      } else if (s.kind === 'subtotal' || s.kind === 'end') {
        bar = Math.abs(raw);
        running = bar;
      } else if (s.kind === 'loss') {
        bar = Math.abs(raw);
        running = Math.max(0, running - bar);
        spacer = running;
      } else if (s.kind === 'gain') {
        bar = Math.abs(raw);
        spacer = running;
        running += bar;
      }
      return {
        label: s.label,
        kind: s.kind,
        value: raw,
        subLabel: s.subLabel || null,
        color: s.color || WATERFALL_PALETTE[s.kind] || '#94a3b8',
        bar, spacer,
      };
    }).map((row, j, arr) => {
      // One dataKey per step (v0…vN) — each populated only on its own row.
      // This lets every step be its own series so per-step colours render
      // (the old per-KIND series painted all 'loss' bars with the FIRST
      // loss step's colour — grid curtailment showed red instead of blue).
      arr.forEach((_, i) => { row[`v${i}`] = i === j ? row.bar : null; });
      return row;
    });
  }, [steps]);

  // Build the series array — only include kinds present in the data so the
  // chart doesn't render empty stack slots. Spacer is always first so visible
  // bars stack on top of it.
  const series = useMemo(() => {
    if (!rows.length) return [];
    const sign = (k) => (k === 'loss' ? '−' : k === 'gain' ? '+' : '');
    return [
      // Spacer ALWAYS first — keeps it transparent and below every bar.
      {
        id: 'spacer', dataKey: 'spacer', stack: 'wf',
        color: 'transparent', label: '',
        valueFormatter: () => null,
      },
      // One series per STEP (not per kind) so each bar renders the step's
      // own colour. The legend is hidden, so the extra series are free; the
      // per-kind label is kept for anything that reads series metadata.
      ...rows.map((r, i) => ({
        id: `s${i}`,
        dataKey: `v${i}`,
        stack: 'wf',
        color: r.color,
        label: seriesLabels[r.kind] || r.kind,
        valueFormatter: (v) => (v == null ? null : `${sign(r.kind)}${formatValue(v)}`),
      })),
    ];
  }, [rows, formatValue, seriesLabels]);

  // ── Axis-trigger tooltip ─────────────────────────────────────────────
  // Fires on the whole column hover area (not just the visible coloured
  // bar), so empty space between pillars still surfaces the value. This
  // fixes the long-standing "tooltip dead-zone" complaint on waterfalls.
  const TooltipBody = () => {
    const data = useAxesTooltip();
    if (!data?.length) return null;
    const idx = data[0]?.dataIndex;
    if (idx == null || idx < 0) return null;
    const row = rows[idx];
    if (!row) return null;
    const sign = row.kind === 'loss' ? '−' : row.kind === 'gain' ? '+' : '';
    return (
      <Box sx={{
        bgcolor: colors.surface || 'background.paper',
        border: '1px solid', borderColor: colors.hairline2 || 'divider',
        borderRadius: 1.5,
        boxShadow: '0 6px 18px rgba(0,0,0,0.16)',
        px: 1.5, py: 1, minWidth: 180,
      }}>
        <Stack direction="row" alignItems="center" spacing={0.85} sx={{ mb: 0.4 }}>
          <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: row.color }} />
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'text.primary' }}>
            {row.label}
          </Typography>
        </Stack>
        <Typography sx={{
          fontSize: '0.95rem', fontWeight: 700,
          fontVariantNumeric: 'tabular-nums', color: row.color,
        }}>
          {sign}{formatValue(Math.abs(row.value))}
        </Typography>
      </Box>
    );
  };
  const Tooltip = (props) => (
    <ChartsTooltipContainer {...props} trigger="axis">
      <TooltipBody />
    </ChartsTooltipContainer>
  );

  // ── Chart-only render path ───────────────────────────────────────────
  // The component supports two modes:
  //   1. embedded chart only — pass `steps`, render the BarChart bare so
  //      caller can wrap in their own Card/header/layout
  //   2. card mode — pass `title`/`caption`/`summary` and the component
  //      wraps the chart in a styled Card
  // Mode is picked by whether `title` is provided.
  const chart = (() => {
    if (loading) {
      return <Skeleton variant="rectangular" height={height}
                       sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />;
    }
    if (!rows.length) {
      return (
        <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="text.disabled">{emptyMessage}</Typography>
        </Box>
      );
    }
    // When a caller is driving the highlight externally (e.g. table row
    // hover), tag every visible series with `highlightScope` so MUI X
    // knows to fade the non-highlighted bars. We only attach it in this
    // mode — native hover keeps the default behaviour otherwise.
    const seriesWithHighlight = highlightedItem
      ? series.map((s) =>
          s.id === 'spacer'
            ? s
            : { ...s, highlightScope: { highlighted: 'item', faded: 'global' } }
        )
      : series;

    return (
      <BarChart
        height={height}
        dataset={rows}
        xAxis={[{
          scaleType: 'band', dataKey: 'label',
          tickLabelStyle: { fontSize: 12, fontWeight: 500, fill: colors.textDim },
        }]}
        yAxis={[{
          width: 72,
          valueFormatter: (v) => formatValue(v),
          tickLabelStyle: { fontSize: 11, fill: colors.textDim },
        }]}
        series={seriesWithHighlight}
        highlightedItem={highlightedItem}
        // Tell MUI X to render bar labels via our custom slot. We use the
        // function-form barLabel so we can drop the spacer; the slot
        // handles positioning above the bar.
        barLabel={(item) => {
          if (item.seriesId === 'spacer') return null;
          if (item.value == null) return null;
          const row = rows[item.dataIndex];
          if (!row) return null;
          const sign = row.kind === 'loss' ? '−' : row.kind === 'gain' ? '+' : '';
          const main = `${sign}${formatValue(Math.abs(row.value))}`;
          // "\n" + subLabel renders as a smaller second line under the value
          // (see AboveBarLabel) — e.g. the step's % of potential.
          return row.subLabel ? `${main}\n${row.subLabel}` : main;
        }}
        slots={{
          tooltip:  Tooltip,
          barLabel: AboveBarLabel,
        }}
        // `hideLegend` is the live API — the old slotProps `legend.hidden`
        // became a no-op after the MUI X upgrade, which is how the legend
        // leaked back in showing one per-kind entry per bar ("Curtailed",
        // "Constraint", …). The axis labels already name every step.
        hideLegend
        slotProps={{
          tooltip: { trigger: 'axis' },
        }}
        sx={{
          '& .MuiChartsAxis-line': { stroke: colors.hairline2 },
          '& .MuiChartsAxis-tick': { stroke: colors.hairline2 },
          '& .MuiChartsGrid-line': { stroke: colors.hairline },
        }}
      />
    );
  })();

  if (!title) return chart;

  return (
    <Card sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          {title}
        </Typography>
        {summary}
      </Stack>
      {caption && (
        <Typography variant="caption" sx={{ color: 'text.secondary', mb: 2 }}>
          {caption}
        </Typography>
      )}
      {chart}
    </Card>
  );
}
